import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { reviewApi, type ReviewQuote, type ReviewSource } from '../api/review'
import { getAuthors } from '../lib/api'
import { ReviewQuoteItem } from '../components/ReviewQuoteItem'
import { ReviewSourceItem } from '../components/ReviewSourceItem'
import { useAuth } from '../context/AuthContext'
import { useIsDesktop } from '../hooks/useIsDesktop'
import type { Author } from '../types'

const PAGE_SIZE = 20

export const ReviewCenter = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isLoggedIn } = useAuth()
  const isDesktop = useIsDesktop()

  const [selected, setSelected] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [extraAuthors, setExtraAuthors] = useState<Author[]>([])

  const statsQuery = useQuery({
    queryKey: ['review', 'stats'],
    queryFn: reviewApi.getStats,
    enabled: isLoggedIn,
  })

  const categories = statsQuery.data?.categories ?? []
  // Beim ersten Laden auf die erste Kategorie mit offenen Punkten springen
  const active =
    selected ?? categories.find((c) => c.count > 0)?.key ?? categories[0]?.key ?? null
  const activeCategory = categories.find((c) => c.key === active)

  const itemsQuery = useQuery({
    queryKey: ['review', 'items', active, page],
    queryFn: () => reviewApi.getItems(active as string, page, PAGE_SIZE),
    enabled: isLoggedIn && !!active,
  })

  const authorsQuery = useQuery({
    queryKey: ['authors'],
    queryFn: () => getAuthors().then((res) => res.data),
    enabled: isLoggedIn,
  })

  const authors = [...(authorsQuery.data ?? []), ...extraAuthors]
  const openTotal = categories.reduce((sum, c) => sum + c.count, 0)

  // Wenn die letzte Seite leer wird, weil alles erledigt wurde, zurückblättern
  const items = itemsQuery.data?.items ?? []
  useEffect(() => {
    if (itemsQuery.data && items.length === 0 && page > 1) {
      setPage((current) => current - 1)
    }
  }, [itemsQuery.data, items.length, page])

  const selectCategory = (key: string) => {
    setSelected(key)
    setPage(1)
  }

  const handleSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['review'] })
    queryClient.invalidateQueries({ queryKey: ['quotes'] })
    queryClient.invalidateQueries({ queryKey: ['sources'] })
  }

  if (!isLoggedIn) {
    return (
      <div className="review-center review-center-message">
        <p>Bitte melde dich an, um das Bearbeitungscenter zu nutzen.</p>
        <button type="button" className="btn btn-primary" onClick={() => navigate('/')}>
          Zur Startseite
        </button>
      </div>
    )
  }

  if (!isDesktop) {
    return (
      <div className="review-center review-center-message">
        <h1>Bearbeitungscenter</h1>
        <p>
          Das Bearbeitungscenter ist für große Bildschirme gedacht. Öffne es am Desktop, um
          unvollständige Datensätze zu vervollständigen.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => navigate('/')}>
          Zurück zu Marginalia
        </button>
      </div>
    )
  }

  const totalPages = itemsQuery.data ? Math.ceil(itemsQuery.data.total / PAGE_SIZE) : 1

  return (
    <div className="review-center">
      <header className="review-header">
        <div className="review-header-title">
          <h1>Bearbeitungscenter</h1>
          <span className="review-header-count">
            {statsQuery.isLoading
              ? 'wird geladen…'
              : `${openTotal} offene${openTotal === 1 ? 'r Punkt' : ' Punkte'}`}
          </span>
        </div>
        <button type="button" className="review-leave" onClick={() => navigate('/')}>
          Verlassen
        </button>
      </header>

      <div className="review-body">
        <aside className="review-sidebar">
          {categories.map((category) => (
            <button
              key={category.key}
              type="button"
              className={`review-tab${category.key === active ? ' active' : ''}`}
              onClick={() => selectCategory(category.key)}
              title={category.hint}
            >
              <span className="review-tab-label">{category.label}</span>
              <span className={`review-tab-count${category.count === 0 ? ' is-done' : ''}`}>
                {category.count}
              </span>
            </button>
          ))}
        </aside>

        <main className="review-content">
          {activeCategory && (
            <div className="review-content-head">
              <h2>{activeCategory.label}</h2>
              <p>{activeCategory.hint}</p>
            </div>
          )}

          {itemsQuery.isLoading && <p className="review-note">Wird geladen…</p>}
          {itemsQuery.isError && (
            <p className="review-error">Die Datensätze konnten nicht geladen werden.</p>
          )}

          {!itemsQuery.isLoading && items.length === 0 && (
            <p className="review-note">Hier ist alles vollständig. 🎉</p>
          )}

          {items.map((item) =>
            itemsQuery.data?.kind === 'quote' ? (
              <ReviewQuoteItem
                key={item.id}
                quote={item as ReviewQuote}
                category={active as string}
                onSaved={handleSaved}
              />
            ) : (
              <ReviewSourceItem
                key={item.id}
                source={item as ReviewSource}
                authors={authors}
                onSaved={handleSaved}
                onAuthorCreated={(author) => setExtraAuthors((prev) => [...prev, author])}
              />
            )
          )}

          {totalPages > 1 && (
            <div className="review-pagination">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Zurück
              </button>
              <span>
                Seite {page} von {totalPages}
              </span>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
              >
                Weiter
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
